import type { ReactNode } from "react";
import { can } from "@sts/schemas";
import type { Me } from "../api/types.js";
import { useSession, usePortal } from "./context.js";
import { useAsync } from "./use-async.js";
import { href, useRoute, type Route } from "./router.js";
import { SignIn } from "../screens/SignIn.jsx";
import { Pipeline } from "../screens/Pipeline.jsx";
import { HandoffConnection } from "../screens/HandoffConnection.jsx";
import { CountersignScreen } from "../screens/Countersign.jsx";
import { Surveys } from "../screens/Surveys.jsx";
import { SurveyOverview } from "../screens/SurveyOverview.jsx";
import { SurveyRooms } from "../screens/SurveyRooms.jsx";
import { SurveyRoom } from "../screens/SurveyRoom.jsx";
import { Deadlines } from "../screens/Deadlines.jsx";
import { Installation } from "../screens/Installation.jsx";
import { Installations } from "../screens/Installations.jsx";
import { JobsMap } from "../screens/JobsMap.jsx";
import { Job } from "../screens/Job.jsx";
import { Survey } from "../screens/Survey.jsx";
import { SurveyStock } from "../screens/SurveyStock.jsx";
import { SiteSurvey } from "../screens/SiteSurvey.jsx";
import { JobDocuments } from "../screens/Documents.jsx";
import { DocumentFinder } from "../screens/DocumentFinder.jsx";
import { Contacts } from "../screens/Contacts.jsx";
import { NewLead } from "../screens/NewLead.jsx";
import { Tasks } from "../screens/Tasks.jsx";
import { Retrofit } from "../screens/Retrofit.jsx";
import { RetrofitProjects } from "../screens/RetrofitProjects.jsx";
import { Chat, Conversation } from "../screens/Chat.jsx";
import { Notifications } from "../screens/Notifications.jsx";
import { TeamScreen } from "../screens/Team.jsx";
import { CompanyScreen } from "../screens/Company.jsx";
import { RecycleBin } from "../screens/RecycleBin.jsx";
import { JobContractScreen } from "../screens/Contract.jsx";
import { JobPhotos } from "../screens/Evidence.jsx";
import { BusGrant } from "../screens/BusGrant.jsx";
import { HeatPumpSelection } from "../screens/HeatPumpSelection.jsx";
import { EmitterSelection } from "../screens/EmitterSelection.jsx";
import { HelpCentre, HelpGuidePage } from "../screens/Help.jsx";
import { AcceptInvitation, ResetPassword } from "../screens/AccountLinks.jsx";
import { ChatUnreadBadge, NotificationUnreadBadge } from "../components/Unread.jsx";
import {
  IconBell,
  IconClock,
  IconCustomers,
  IconHelp,
  IconInstallations,
  IconMapPin,
  IconMessages,
  IconPipeline,
  IconPlus,
  IconRetrofit,
  IconScannix,
  IconState,
  IconTasks,
  IconCompany,
  IconTeam,
} from "../components/Icons.jsx";

/**
 * The whole application.
 *
 * Sign-in is a branch here rather than a redirect: there is no route to protect
 * because an unauthenticated user is never shown one, which removes the class
 * of bug where a screen mounts, fires its request and only then discovers there
 * is no session.
 *
 * The two emailed links are the exception, and they are checked BEFORE that
 * branch. Somebody following an invitation has no session and no account — the
 * link is what creates one — so sending them to a sign-in form would ask for a
 * password they have never had. They are checked before the signed-in branch
 * too, because a person who is signed in as somebody else and opens their own
 * invitation means the link, not the session they happen to be holding.
 */
export function App(): ReactNode {
  const session = useSession();
  const linked = useRoute();
  if (linked.name === "invitation") {
    return <AcceptInvitation token={linked.token} />;
  }
  if (linked.name === "reset-password") {
    return <ResetPassword token={linked.token} />;
  }
  if (!session) return <SignIn />;
  return (
    <div className="app">
      <a className="skip-link" href="#main">
        Skip to content
      </a>
      <Rail />
      <main className="app__main" id="main">
        <div className="app__canvas">
          <Screen />
        </div>
      </main>
    </div>
  );
}

function Screen(): ReactNode {
  const route = useRoute();
  switch (route.name) {
    case "surveys":
      return <Surveys />;
    case "pipeline":
      return <Pipeline />;
    case "deadlines":
      return <Deadlines />;
    case "jobs-map":
      return <JobsMap />;
    case "installations":
      return <Installations />;
    case "installation":
      return <Installation jobId={route.jobId} />;
    case "contacts":
      return <Contacts />;
    case "tasks":
      return <Tasks />;
    case "new-lead":
      return <NewLead />;
    case "job":
      return <Job id={route.id} />;
    case "survey-overview":
      return <SurveyOverview id={route.id} />;
    case "survey-room":
      return <SurveyRoom surveyId={route.id} roomId={route.roomId} />;
    case "survey-rooms":
      return <SurveyRooms id={route.id} />;
    // "The drawing" is a door off a survey, so it opens onto the drawing. The
    // screen's full form keeps its own door at /checks — carving the plan out
    // of it must not orphan the readiness checks, the conditions, the
    // materials, the heat loss and the assumptions.
    case "survey":
      return <Survey id={route.id} show="plan" />;
    case "survey-checks":
      return <Survey id={route.id} show="all" />;
    case "handoff":
      return <HandoffConnection />;
    case "countersign":
      return <CountersignScreen />;
    case "survey-stock":
      return <SurveyStock surveyId={route.id} />;
    case "survey-site":
      return <SiteSurvey surveyId={route.id} section={route.section} />;
    case "job-documents":
      return <JobDocuments jobId={route.jobId} />;
    case "document-finder":
      return <DocumentFinder />;
    case "retrofits":
      return <RetrofitProjects />;
    case "retrofit":
      return <Retrofit id={route.id} />;
    case "chat":
      return <Chat />;
    case "chat-thread":
      return <Conversation id={route.id} />;
    case "notifications":
      return <Notifications />;
    case "team":
      return <TeamScreen />;
    case "company":
      return <CompanyScreen />;
    case "recycle-bin":
      return <RecycleBin />;
    case "job-contract":
      return <JobContractScreen jobId={route.jobId} />;
    case "job-photos":
      return <JobPhotos jobId={route.jobId} />;
    case "bus-grant":
      return <BusGrant jobId={route.jobId} />;
    case "heat-pump":
      return <HeatPumpSelection jobId={route.jobId} />;
    case "emitters":
      return <EmitterSelection jobId={route.jobId} />;
    case "help":
      return <HelpCentre />;
    case "help-guide":
      return <HelpGuidePage id={route.id} />;
    // Reached only if `App` above ever stops intercepting these two. Kept
    // rather than folded into "unknown" so the emailed link still works rather
    // than reporting itself as not a page.
    case "invitation":
      return <AcceptInvitation token={route.token} />;
    case "reset-password":
      return <ResetPassword token={route.token} />;
    case "unknown":
      return (
        <div className="state state--empty">
          <span className="state__glyph">
            <IconState kind="empty" />
          </span>
          <p className="state__title">Not a page</p>
          <p className="state__body">
            Nothing is routed at <span className="mono">{route.path}</span>.
          </p>
          <a className="cta" href={href.surveys()}>
            Back to the surveys
          </a>
        </div>
      );
  }
}

/**
 * The application rail.
 *
 * This used to be a vertical `<ul>` of nine links crammed into the top-left
 * corner with the signed-in address floating beside it. It is now a fixed rail:
 * the wordmark, the one act that creates work ("New lead") as a filled button,
 * the destinations grouped by what they are for, and the account anchored at
 * the bottom where it stops competing with navigation.
 *
 * Grouping is the substance of the change, not decoration. Nine equal links
 * make the reader scan all nine every time; four labelled groups of two or
 * three mean the eye goes to one group and reads inside it.
 */
/**
 * What the rail offers an installer.
 *
 * NOT DERIVED FROM THE PERMISSION MATRIX, and that is the point worth saying.
 * An installer may read their own job — they have to, to photograph it — so a
 * matrix-driven rail would show them Pipeline, and Pipeline is not an
 * installer's tool. The matrix answers "may they", this answers "is it theirs
 * to do". They are different questions and conflating them is how an installer
 * ended up looking at the whole company's pipeline board.
 *
 * THIS IS NOT THE SECURITY BOUNDARY. Hiding a link does not stop anybody
 * typing the URL, and it is not meant to: every screen checks its own
 * permission and the API refuses whatever the matrix refuses, against the
 * token, where nothing on this side can reach. This decides what is worth
 * drawing for the person whose whole job in this portal is one board.
 */
const INSTALLER_SECTIONS: ReadonlySet<string> = new Set(["installations"]);

/** The sections the survey-only rail offers at all. */
/**
 * Every section the rail offers.
 *
 * It was cut to three while this was a survey-only tool, and the ten
 * destinations that went are back by request. The list stays explicit rather
 * than being deleted: it is what stops a new route appearing in an
 * installer's rail by default, which `INSTALLER_SECTIONS` above then narrows
 * to the one section that is their whole job.
 */
const SURVEY_SECTIONS: ReadonlySet<string> = new Set([
  "surveys",
  "pipeline",
  "tasks",
  "deadlines",
  // The company's half of a signature. On the rail because an agreement
  // waiting to be countersigned is work somebody has to notice -- a customer
  // has signed and is holding a half-executed contract until they do.
  "countersign",
  "installations",
  "jobs-map",
  "contacts",
  "retrofit",
  "chat",
  "notifications",
  "team",
  "company",
  "help",
]);

function Rail(): ReactNode {
  const { client, session } = usePortal();
  const current = useSession();
  const route = useRoute();
  // The role is not in the session — it is the server's answer, asked for
  // once here. Until it arrives NOTHING is drawn, deliberately: a rail that
  // showed everything for a moment and then collapsed would flash the whole
  // company's navigation at an installer every time they opened the portal.
  const { state: me } = useAsync<Me>(() => client.me(), [client]);
  const role = me.status === "ready" ? me.data.role : null;
  const installer = role === "installer";
  const shows = (section: string): boolean =>
    role !== null &&
    SURVEY_SECTIONS.has(section) &&
    (!installer || INSTALLER_SECTIONS.has(section));

  async function signOut(): Promise<void> {
    // The local session is cleared whatever the API says. A network failure
    // must not be able to keep somebody signed in on a shared laptop, and the
    // token is individually revocable server-side in any case.
    try {
      await client.logout();
    } catch {
      // Deliberately swallowed; see above.
    } finally {
      session.clear("You signed out.");
    }
  }

  // `aria-current` is what paints the active state, so the indicator a sighted
  // reader sees and the one a screen reader announces cannot disagree.
  const active = activeSection(route.name);

  return (
    <header className="rail">
      <a className="rail__brand" href={href.surveys()}>
        <span className="rail__mark">
          <IconScannix />
        </span>
        <span>
          <span className="rail__wordmark">Scannix</span>
          <span className="rail__tagline">Survey portal</span>
        </span>
      </a>

      {/* "New lead" is sales language, and a surveyor is not taking a lead:
          they are adding a house that needs walking. Surveys normally arrive
          from the phone on their own, so this is the exception rather than the
          thing the screen is for. */}
      {role !== null && can({ role, resource: "job", action: "create" }) ? (
        <a className="rail__cta" href={href.newLead()}>
          <IconPlus />
          Add a house
        </a>
      ) : null}

      {/*
       * THE WHOLE NAVIGATION, in five groups.
       *
       * This was cut to three destinations when the portal was a survey-only
       * tool: the argument was that for a surveyor the other ten were
       * somebody else's work. The routes were always left intact, so nothing
       * had to be rebuilt to put them back — the screens had simply lost
       * their doors, and Team in particular could only be reached by typing
       * the URL, which is no way to run an organisation.
       *
       * WHAT STILL GATES IT. `shows` asks three questions: whether the
       * server has answered with a role at all (nothing is drawn before it
       * has, so an installer never sees the whole company's navigation flash
       * past), whether the section is in the list, and whether an installer
       * is allowed it. An installer still gets Installations and nothing
       * else, which is their whole job.
       */}
      <nav className="rail__nav" aria-label="Primary">
        {role === null ? null : (
          <RailGroup label="Work">
            {shows("surveys") ? (
              <RailLink
                href={href.surveys()}
                label="Surveys"
                icon={<IconPipeline />}
                active={active === "surveys"}
              />
            ) : null}
            {shows("pipeline") ? (
              <RailLink
                href={href.pipeline()}
                label="Pipeline"
                icon={<IconPipeline />}
                active={active === "pipeline"}
              />
            ) : null}
            {shows("tasks") ? (
              <RailLink
                href={href.tasks()}
                label="Tasks"
                icon={<IconTasks />}
                active={active === "tasks"}
              />
            ) : null}
            {shows("deadlines") ? (
              <RailLink
                href={href.deadlines()}
                label="Deadlines"
                icon={<IconClock />}
                active={active === "deadlines"}
              />
            ) : null}
            {shows("countersign") ? (
              <RailLink
                href={href.countersign()}
                label="Countersign"
                icon={<IconPipeline />}
                active={active === "countersign"}
              />
            ) : null}
            {shows("installations") ? (
              <RailLink
                href={href.installations()}
                label="Installations"
                icon={<IconInstallations />}
                active={active === "installations"}
              />
            ) : null}
            {shows("jobs-map") ? (
              <RailLink
                href={href.jobsMap()}
                label="Jobs map"
                icon={<IconMapPin />}
                active={active === "jobs-map"}
              />
            ) : null}
          </RailGroup>
        )}

        {shows("contacts") ? (
          <RailGroup label="Records">
            <RailLink
              href={href.contacts()}
              label="Customers"
              icon={<IconCustomers />}
              active={active === "contacts"}
            />
            <RailLink
              href={href.retrofits()}
              label="Retrofit"
              icon={<IconRetrofit />}
              active={active === "retrofit"}
            />
          </RailGroup>
        ) : null}

        {shows("chat") ? (
          <RailGroup label="Inbox">
            {/* The badge lives inside the link so a screen reader announces
                "Messages, 3 unread" as one destination rather than as two
                unrelated pieces of furniture. */}
            <RailLink
              href={href.chat()}
              label="Messages"
              icon={<IconMessages />}
              active={active === "chat"}
              badge={<ChatUnreadBadge />}
            />
            <RailLink
              href={href.notifications()}
              label="Notifications"
              icon={<IconBell />}
              active={active === "notifications"}
              badge={<NotificationUnreadBadge />}
            />
          </RailGroup>
        ) : null}

        {shows("team") ? (
          <RailGroup label="Organisation">
            <RailLink
              href={href.team()}
              label="Team"
              icon={<IconTeam />}
              active={active === "team"}
            />
            <RailLink
              href={href.company()}
              label="Company"
              icon={<IconCompany />}
              active={active === "company"}
            />
            <RailLink
              href={href.recycleBin()}
              label="Recycle bin"
              icon={<IconState kind="empty" />}
              active={active === "recycle-bin"}
            />
          </RailGroup>
        ) : null}

        {shows("help") ? (
          <RailGroup label="Support">
            <RailLink
              href={href.help()}
              label="Help"
              icon={<IconHelp />}
              active={active === "help"}
            />
          </RailGroup>
        ) : null}
      </nav>

      <div className="rail__account">
        <span className="rail__avatar" aria-hidden="true">
          {(current?.email ?? "?").slice(0, 1)}
        </span>
        <span className="rail__who identity small">
          <span className="rail__email" title={current?.email}>
            {current?.email}
          </span>
          <button className="rail__signout" onClick={() => void signOut()}>
            Sign out
          </button>
        </span>
      </div>
    </header>
  );
}

/**
 * Which rail entry a route belongs under.
 *
 * Detail routes highlight the section they came from — a job is still the
 * pipeline, a conversation is still Messages — so the rail never goes blank on
 * the screens people spend the most time in.
 */
function activeSection(name: Route["name"]): string | null {
  switch (name) {
    // Everything inside a survey keeps the Surveys item lit: the plan, the
    // stock report, the site survey, the photographs. A reader who follows a
    // link deeper into a survey has not left the section.
    case "surveys":
    case "pipeline":
    case "job":
    case "survey-overview":
    case "survey-checks":
    case "survey-room":
    case "survey-rooms":
    case "survey":
    case "survey-stock":
    case "survey-site":
    case "job-documents":
    case "job-photos":
    case "bus-grant":
    case "heat-pump":
    case "emitters":
    case "document-finder":
    case "handoff":
    case "countersign":
      return "surveys";
    case "tasks":
      return "tasks";
    case "deadlines":
      return "deadlines";
    case "jobs-map":
      return "jobs-map";
    case "installations":
    // The detail screen keeps the Installations item lit. That is the whole
    // point of it having its own route: you never leave the section.
    case "installation":
      return "installations";
    case "contacts":
      return "contacts";
    case "retrofits":
    case "retrofit":
      return "retrofit";
    case "chat":
    case "chat-thread":
      return "chat";
    case "notifications":
      return "notifications";
    case "team":
      return "team";
    case "company":
      return "company";
    case "recycle-bin":
      return "recycle-bin";
    case "help":
    case "help-guide":
      return "help";
    default:
      return null;
  }
}

function RailGroup({
  label,
  children,
}: {
  readonly label: string;
  readonly children: ReactNode;
}): ReactNode {
  return (
    <div>
      <p className="rail__group-label">{label}</p>
      <ul className="rail__list">{children}</ul>
    </div>
  );
}

function RailLink({
  href: to,
  label,
  icon,
  active,
  badge,
}: {
  readonly href: string;
  readonly label: string;
  readonly icon: ReactNode;
  readonly active: boolean;
  readonly badge?: ReactNode;
}): ReactNode {
  return (
    <li>
      <a
        className="rail__link"
        href={to}
        aria-current={active ? "page" : undefined}
      >
        {icon}
        {label}
        {badge ? <span className="rail__badge">{badge}</span> : null}
      </a>
    </li>
  );
}
